import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

export function ReservationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('reservations:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('reservations:write'));
  const parkings = useQuery({ queryKey: ['parkings'], queryFn: () => api.listParkings() });
  const list = useQuery({
    queryKey: ['reservations'],
    queryFn: () => api.listReservations(),
    enabled: canRead,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    parkingId: '',
    startsAt: new Date().toISOString().slice(0, 16),
    endsAt: new Date(Date.now() + 3600_000).toISOString().slice(0, 16),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createReservation({
        parkingId: form.parkingId,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
      }),
    onSuccess: async () => {
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['reservations'] });
    },
  });

  const act = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'confirm' | 'cancel' | 'check-in' }) => {
      if (action === 'confirm') return api.confirmReservation(id);
      if (action === 'cancel') return api.cancelReservation(id);
      return api.checkInReservation(id);
    },
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['reservations'] }),
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('reservations.forbidden')}</div>;
  }

  const rows = list.data?.data ?? [];
  const columns = [
    t('reservations.status'),
    t('reservations.starts'),
    t('reservations.ends'),
    'QR',
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
              {t('reservations.create')}
            </button>
          ) : undefined
        }
      />

      {list.isError && <div className="alert alert-danger">{(list.error as Error).message}</div>}

      <Card>
        <ParkingDataTable
          storageKey="reservations"
          columns={columns}
          refreshKey={rows.map((r) => `${r.id}:${r.status}`).join('|')}
          actionsColumn={canWrite}
          galleryItems={rows.map((r) => ({
            id: r.id,
            icon: 'bi-calendar2-check',
            iconTone: statusToneFromEnum(r.status),
            title: new Date(r.startsAt).toLocaleString(),
            subtitle: `→ ${new Date(r.endsAt).toLocaleString()}`,
            meta: `QR: ${r.qrCode ?? '—'}`,
            badge: (
              <StatusBadge tone={statusToneFromEnum(r.status)}>
                {enumLabel(t, 'reservationStatus', r.status)}
              </StatusBadge>
            ),
            actions: canWrite ? (
              <div className="d-flex gap-1 justify-content-center flex-wrap">
                {r.status === 'PENDING' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => act.mutate({ id: r.id, action: 'confirm' })}
                  >
                    {t('reservations.confirm')}
                  </button>
                )}
                {['PENDING', 'CONFIRMED'].includes(r.status) && (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => act.mutate({ id: r.id, action: 'check-in' })}
                    >
                      {t('reservations.checkIn')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => act.mutate({ id: r.id, action: 'cancel' })}
                    >
                      {t('reservations.cancel')}
                    </button>
                  </>
                )}
              </div>
            ) : undefined,
          }))}
        >
          {rows.map((r) => (
            <tr key={r.id}>
              <td>
                <StatusBadge tone={statusToneFromEnum(r.status)}>
                  {enumLabel(t, 'reservationStatus', r.status)}
                </StatusBadge>
              </td>
              <td>{new Date(r.startsAt).toLocaleString()}</td>
              <td>{new Date(r.endsAt).toLocaleString()}</td>
              <td>{r.qrCode ?? '—'}</td>
              {canWrite && (
                <td>
                  <div className="d-flex gap-1 justify-content-center flex-wrap">
                    {r.status === 'PENDING' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => act.mutate({ id: r.id, action: 'confirm' })}
                      >
                        {t('reservations.confirm')}
                      </button>
                    )}
                    {['PENDING', 'CONFIRMED'].includes(r.status) && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={() => act.mutate({ id: r.id, action: 'check-in' })}
                        >
                          {t('reservations.checkIn')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={() => act.mutate({ id: r.id, action: 'cancel' })}
                        >
                          {t('reservations.cancel')}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('reservations.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!form.parkingId || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          <div>
            <label className="form-label">{t('reservations.parking')}</label>
            <select
              className="form-select"
              value={form.parkingId}
              onChange={(e) => setForm({ ...form, parkingId: e.target.value })}
            >
              <option value="">—</option>
              {(parkings.data?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('reservations.starts')}</label>
            <input
              className="form-control"
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('reservations.ends')}</label>
            <input
              className="form-control"
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
            />
          </div>
        </div>
      </AppModal>
    </>
  );
}
